import { PGGIcon } from '@components/icons/PGGIcon'
import { CurrencyFormat } from '@components/shared/Format'
import { FormField, SelectOption } from '@components/shared/FormField'
import { GoldCard } from '@components/shared/GoldCard'
import { I18n } from '@components/shared/I18n'
import { InfoBar } from '@components/shared/InfoBar'
import { WithTooltip } from '@components/shared/WithTooltip'
import { PTB_LEG_TIERS } from '@models/Constants'
import { CountryInfoUrlKey } from '@models/CountryInfo'
import { SectionComponent, SectionKey } from '@sections/SectionComponent'
import { StoreProps } from '@stores/Store'
import { I18nKey } from '@utils/i18n'
import { Form, FormInstance } from 'antd'
import { inject, observer } from 'mobx-react'
import React, { createRef } from 'react'
import styles from './PathToBronzeSection.module.scss'

// The '-' dropdown option, no payout until the user picks a tier
const PTB_LEGS_NONE = 0

// Every market's Path to Bronze amount is a whole number, so the requirements
// tier list and the dropdown options render without decimals (Amway request,
// 2026-08-25). The reward card is deliberately not included, it keeps the shared
// 2-decimal GoldCard formatting the other sections use.
const PTB_DECIMAL_SCALE = 0

interface PathToBronzeFormData {
  pathToBronzeSelectedLegs: number
}

@inject('store')
@observer
export class PathToBronzeSection extends SectionComponent<StoreProps, {}> {
  static defaultProps = {} as StoreProps
  static get title(): I18nKey { return 'SECTION_PATH_TO_BRONZE_INCENTIVE' }
  static get icon() { return <PGGIcon /> }
  static get trackerKey() { return 'C+pathtobronze' }

  formRef = createRef<FormInstance>()

  componentDidMount() {
    this.syncFormToEffectiveLegs()
  }

  componentDidUpdate() {
    this.syncFormToEffectiveLegs()
  }

  // antd forms are uncontrolled after mount (initialValues are read once), while
  // the effective tier depends on state the form does not own, the persisted
  // selection hydrates asynchronously and frontline edits can shrink eligibility
  // below a selection. Push the effective (clamped) tier back into the field so
  // the dropdown always displays the tier the payout actually uses.
  syncFormToEffectiveLegs() {
    const { pathToBronzeEffectiveLegs } = this.props.store.pathToBronzeCalculation
    const formValue = pathToBronzeEffectiveLegs ?? PTB_LEGS_NONE
    const form = this.formRef.current
    if (form && form.getFieldValue('pathToBronzeSelectedLegs') !== formValue) {
      form.setFieldsValue({ pathToBronzeSelectedLegs: formValue })
    }
  }

  onValuesChange = (data: PathToBronzeFormData) => {
    const { store } = this.props
    const { pathToBronzeStore } = store
    if ('pathToBronzeSelectedLegs' in data) {
      pathToBronzeStore.setPathToBronzeSelectedLegs(data.pathToBronzeSelectedLegs === PTB_LEGS_NONE ? null : data.pathToBronzeSelectedLegs)
    }
    store.globalStore.updateSectionUsage(SectionKey.PATH_TO_BRONZE)
  }

  render() {
    const { store } = this.props
    const { pathToBronzeCalculation, localizationHelper } = store
    const { pathToBronzeEligibleLegs, pathToBronzeEffectiveLegs, pathToBronzeMonthlyAmount } = pathToBronzeCalculation
    const { PATH_TO_BRONZE_ELIGIBILITY_KEY } = localizationHelper

    const tierAmounts = PTB_LEG_TIERS.map((legs) => ({ legs, amount: pathToBronzeCalculation.pathToBronzeAmountForLegs(legs) }))
    const amountOptions: SelectOption<number>[] = [
      { label: '-', value: PTB_LEGS_NONE },
      ...tierAmounts
        .filter(({ legs }) => pathToBronzeEligibleLegs.includes(legs))
        .map(({ legs, amount }) => ({ label: <CurrencyFormat value={amount} decimalScale={PTB_DECIMAL_SCALE} />, value: legs }))
    ]

    return (
      <>
        <InfoBar divider={false}>
          <WithTooltip title='PATH_TO_BRONZE_TOOLTIP' iconPosition='right' iconFloat='right'><I18n name='PATH_TO_BRONZE_REQUIREMENTS' /></WithTooltip>
        </InfoBar>
        <div className='section'>
          <ul className={styles['requirements']}>
            <li><I18n name={PATH_TO_BRONZE_ELIGIBILITY_KEY} /></li>
            <li><I18n name='PATH_TO_BRONZE_PPV_REQUIREMENT' /></li>
            <li>
              <I18n name='PATH_TO_BRONZE_LEGS_REQUIREMENT' />
              <div className={styles['tier-amounts']}>
                {tierAmounts.map(({ legs, amount }) => (
                  <div key={legs} className={styles['tier']}>
                    <span className={styles['tier-legs']}>{legs}:</span>
                    <span><CurrencyFormat value={amount} decimalScale={PTB_DECIMAL_SCALE} /></span>
                  </div>
                ))}
              </div>
            </li>
          </ul>
        </div>
        <Form ref={this.formRef} className='section section-footer' layout='vertical'
          initialValues={{ pathToBronzeSelectedLegs: pathToBronzeEffectiveLegs ?? PTB_LEGS_NONE }}
          onValuesChange={this.onValuesChange}>
          <FormField name='pathToBronzeSelectedLegs' type='select' label='SET_MONTHLY_AMOUNT' options={amountOptions} />
        </Form>
        <div className='section section-rewards'>
          {pathToBronzeMonthlyAmount === 0 ? (
            <GoldCard title='CP_PATH_TO_BRONZE_INCENTIVE_DISABLED' disabled infoContent='PATH_TO_BRONZE_TOOLTIP' infoUrl={CountryInfoUrlKey.INFO_PATH_TO_BRONZE_INCENTIVE_URL}></GoldCard>
          ) : (
            <GoldCard title='CP_PATH_TO_BRONZE_INCENTIVE' value={pathToBronzeMonthlyAmount} period='PER_MONTH' footer={
              // Annual shows no amount, only the 12-payments hover tooltip, same treatment as the summary row
              <span className={styles['annual']}><WithTooltip title='PATH_TO_BRONZE_TOOLTIP' iconPosition='right'><I18n name='ANNUAL_INCOME' /></WithTooltip></span>
            }></GoldCard>
          )}
        </div>
      </>
    )
  }
}
